// Lightweight date/time formatting via Intl (no date library dependency).

const weekday = new Intl.DateTimeFormat('en-US', { weekday: 'short' })
const timeOfDay = new Intl.DateTimeFormat('en-US', { hour: 'numeric', minute: '2-digit' })
const rel = new Intl.RelativeTimeFormat('en-US', { numeric: 'auto' })

// Clock-format–aware variants (driven by the 12h/24h preference). Cached per format.
type Clock = '12h' | '24h'
const hourFmt: Record<Clock, Intl.DateTimeFormat> = {
  '12h': new Intl.DateTimeFormat('en-US', { hour: 'numeric', hour12: true }),
  '24h': new Intl.DateTimeFormat('en-US', { hour: 'numeric', hourCycle: 'h23' }),
}
const timeFmt: Record<Clock, Intl.DateTimeFormat> = {
  '12h': new Intl.DateTimeFormat('en-US', { hour: 'numeric', minute: '2-digit', hour12: true }),
  '24h': new Intl.DateTimeFormat('en-US', { hour: '2-digit', minute: '2-digit', hourCycle: 'h23' }),
}

/** "3 PM" / "15" — hour label honouring the clock preference. */
export const fmtHourClock = (iso: string | Date, clock: Clock) =>
  hourFmt[clock].format(typeof iso === 'string' ? new Date(iso) : iso)

/** "3:42 PM" / "15:42" — full time honouring the clock preference. */
export const fmtTimeClock = (iso: string | Date, clock: Clock) =>
  timeFmt[clock].format(typeof iso === 'string' ? new Date(iso) : iso)

/** "Mon" */
export const fmtWeekday = (iso: string) => weekday.format(new Date(iso))

/** "3:42 PM" */
export const fmtTime = (iso: string) => timeOfDay.format(new Date(iso))

/** "5 min ago" — coarse relative time for "updated" timestamps. */
export function fmtRelative(iso: string): string {
  const diffMs = new Date(iso).getTime() - Date.now()
  const diffMin = Math.round(diffMs / 60000)
  if (Math.abs(diffMin) < 60) return rel.format(diffMin, 'minute')
  const diffHr = Math.round(diffMin / 60)
  if (Math.abs(diffHr) < 24) return rel.format(diffHr, 'hour')
  return rel.format(Math.round(diffHr / 24), 'day')
}

export const isToday = (iso: string) => {
  const d = new Date(iso)
  const now = new Date()
  return d.toDateString() === now.toDateString()
}
